import { Divider, FormControl, FormHelperText, InputLabel, ListSubheader, MenuItem, Select } from '@mui/material'
import { NextElementType } from '../data/NextElementDisplay'
import { LocalNext } from '../../domain/form/definition/LocalNext'
import { useFieldError } from '../../util/FormErrorBus'
import { useEffect, useMemo } from 'react'
import NextOption from './NextOption'

export type NextElementInputProps = {
  value?: LocalNext
  parentKey: string
  type: NextElementType
  onChange: (newValue: LocalNext | undefined) => void
  options?: readonly NextOption[]
}

const NextElementInput: React.FC<NextElementInputProps> = ({ value, parentKey, type, onChange, options = [] }) => {
  const defaultText = type === 'page' ? 'Next page' : 'Next item'
  const labelId = 'next-element-input-label'

  const { hasError, setError, clearError } = useFieldError(parentKey)
  useEffect(() => {
    const currentOption = value ? options?.find(({ next }) => next.key === value.key)?.label : undefined

    const fieldHasError = value && !currentOption
    if (fieldHasError) setError('')
    else clearError()
  }, [value, options])

  const pageOptions = useMemo(() => options.filter(({ next }) => next.type === 'page'), [options])
  const itemOptions = useMemo(() => options.filter(({ next }) => next.type === 'item'), [options])

  return (
    <FormControl error={hasError}>
      <InputLabel shrink id={labelId}>
        {'Go to'}
      </InputLabel>
      <Select
        labelId={labelId}
        label={'Go to'}
        value={value ?? ''}
        onChange={(e) => {
          const newValue = e.target.value
          if (newValue === '') {
            onChange(undefined)
          } else {
            const nextValue = options.find(({ next }) => next.key === newValue)?.next
            onChange(nextValue)
          }
        }}
        renderValue={(value) => {
          const option = options.find(({ next }) => next.key === value.key)?.label

          return option ?? value.key ?? defaultText
        }}
        displayEmpty
        sx={{ minWidth: 200 }}
      >
        <MenuItem value={''}>{defaultText}</MenuItem>
        {type === 'page' &&
          pageOptions.map(({ next, label }) => (
            <MenuItem key={next.key} value={next.key}>
              {label}
            </MenuItem>
          ))}

        {type === 'item' && itemOptions.length > 0 && (
          <ListSubheader disableSticky>Elements on this page</ListSubheader>
        )}
        {type === 'item' &&
          itemOptions.map(({ next, label }) => (
            <MenuItem key={next.key} value={next.key}>
              {label}
            </MenuItem>
          ))}
        {type === 'item' && pageOptions.length > 0 && <Divider component="li" sx={{ my: 0.5 }} />}
        {type === 'item' && pageOptions.length > 0 && <ListSubheader disableSticky>Other pages</ListSubheader>}
        {type === 'item' &&
          pageOptions.map(({ next, label }) => (
            <MenuItem key={next.key} value={next.key}>
              {label}
            </MenuItem>
          ))}
      </Select>
      {hasError && <FormHelperText error>{`Invalid ${value?.type} selected`}</FormHelperText>}
    </FormControl>
  )
}

export default NextElementInput
